import { Injectable } from '@angular/core';
import { ApiEntityService } from '@core/api/api-entity.service';
import { Automapper } from '@core/api/mapper';
import { DownloaderService } from '@core/downloader/downloader.service';
import { Product } from '@core/models/product.model';
import { UserNamespacedResourceBaseUrlBuilder } from '@core/user-namespaced-resource-base-url-builder.service';
import { convertBlobToBase64, normalizeDate, removeNilProperties } from '@core/utils';
import { TranslateService } from '@ngx-translate/core';
import { LanguageService } from '@core/language/language.service';
import { forkJoin, Observable, throwError } from 'rxjs';
import { catchError, map, mergeMap, tap } from 'rxjs/operators';

import { ProductState, ProductStore } from './product.store';
import { SnackBarService } from '@feature/snack-bar/snack-bar.service';

type Subresource = '' | 'opening' | 'short-profile' | 'process';

@Injectable({ providedIn: 'root' })
export class ProductService extends ApiEntityService<ProductState> {
  constructor(
    store: ProductStore,
    urlBuilder: UserNamespacedResourceBaseUrlBuilder,
    private downloader: DownloaderService,
    private translate: TranslateService,
    private snackBarService: SnackBarService,
    private languageService: LanguageService,
  ) {
    super(store, urlBuilder, Automapper.create(Product));
  }

  public setLoading(_isLoading: boolean) {
    // No-op by default, implemented by specific services if needed
    // This prevents errors when components try to call setLoading on ProductService
  }

  public getProductImage(imageUrl: string): Observable<string | ArrayBuffer> {
    if (imageUrl) {
      return this.downloader.download(imageUrl).pipe(mergeMap((blob) => convertBlobToBase64(blob)));
    }
  }
  public getProductBanner(imageUrl: string): Observable<string | ArrayBuffer> {
    if (imageUrl) {
      return this.downloader.download(imageUrl).pipe(mergeMap((blob) => convertBlobToBase64(blob)));
    }
  }

  public getWithSubresources(id: Product['id']): Observable<Product> {
    const subresources = [
      this.getPart('', id),
      this.getPart('opening', id),
      this.getPart('short-profile', id),
      this.getPart('process', id),
    ];

    return forkJoin(subresources).pipe(
      map(([product, opening, shortProfile, process]) => {
        const prod = this.mergeProductSubresources(product, opening, shortProfile, process);
        this.store.replace(prod.id, prod);
        return prod;
      }),
    );
  }

  public getWithSubresourcesAndLanguage(
    id: Product['id'],
    language: string,
    includeTranslate?: boolean,
  ): Observable<Product> {
    // Build query parameters - always include lang parameter when language is provided
    const queryParams: any = {};

    // Always include the language parameter for product editing contexts
    if (language) {
      queryParams.lang = language;
      // Add translate=true if requested (for translation operations)
      if (includeTranslate) {
        queryParams.translate = 'true';
      }
    }

    // Use undefined if no parameters to avoid empty object
    const finalQueryParams = Object.keys(queryParams).length > 0 ? queryParams : undefined;

    const subresources = [
      this.getPart('', id, finalQueryParams),
      this.getPart('opening', id, finalQueryParams),
      this.getPart('short-profile', id, finalQueryParams),
      this.getPart('process', id, finalQueryParams),
    ];

    return forkJoin(subresources).pipe(
      map(([product, opening, shortProfile, process]) => {
        const prod = this.mergeProductSubresources(product, opening, shortProfile, process);
        this.store.replace(prod.id, prod);
        return prod;
      }),
      // After translation is completed, update availableTranslations from the main product response
      // The main product response should already have the correct availableTranslations
      map((translatedProduct) => {
        return translatedProduct;
      }),
    );
  }

  public updateOpening(product: Product): Observable<Partial<Product>> {
    product = Product.create({
      ...product,
      productName: product.productName || this.translate.instant('INTL_UNDEFINED_NAME'),
    });

    // image save is handled via dedicated service method
    // as query model has different schema than command model
    delete product.image;
    delete product.bannerImage;
    return this.useUrlSuffix('opening')
      .update<Product>(product.id, product, { skipWrite: true })
      .pipe(
        map((p) => this.removeUnnecessaryProperties(p)),
        tap((p) => {
          p = Product.create({ ...product, ...p });
          this.store.replace(p.id, p);
        }),
      );
  }

  public updateOpeningWithLanguage(product: Product, language: string): Observable<Partial<Product>> {
    product = Product.create({
      ...product,
      productName: product.productName || this.translate.instant('INTL_UNDEFINED_NAME'),
    });

    // image save is handled via dedicated service method
    // as query model has different schema than command model
    delete product.image;
    delete product.bannerImage;

    // Always pass language parameter when editing a product
    // This ensures consistent language context for all product updates
    const params = { lang: language };

    return this.useUrlSuffix('opening')
      .update<Product>(product.id, product, { skipWrite: true, params })
      .pipe(
        map((p) => this.removeUnnecessaryProperties(p)),
        tap((p) => {
          p = Product.create({ ...product, ...p });
          this.store.replace(p.id, p);
        }),
      );
  }

  public updateOpeningImages(product: Product, imageFile: File, bannerFile: File): Observable<Partial<Product>> {
    const formData: FormData = new FormData();
    imageFile && formData.append('image', imageFile);
    bannerFile && formData.append('bannerImage', bannerFile);

    return this.useUrlSuffix('opening')
      .update<Product>(product.id, formData, { skipWrite: true })
      .pipe(
        map((p) => this.removeUnnecessaryProperties(p)),
        tap((p) => {
          p = Product.create({ ...product, ...p });
          this.store.replace(p.id, p);
        }),
      );
  }

  public updateProcess(product: Product): Observable<Partial<Product>> {
    product = Product.create({
      ...product,
      productName: product.productName || this.translate.instant('INTL_UNDEFINED_NAME'),
    });

    return this.useUrlSuffix('process')
      .update<Product>(product.id, product, { skipWrite: true })
      .pipe(
        map((p) => removeNilProperties(p)),
        tap((p) => {
          p = Product.create({ ...product, ...p });
          this.store.replace(p.id, p);
        }),
      );
  }

  public updateProcessWithLanguage(product: Product, language: string): Observable<Partial<Product>> {
    product = Product.create({
      ...product,
      productName: product.productName || this.translate.instant('INTL_UNDEFINED_NAME'),
    });

    // Always pass language parameter when editing a product
    // This ensures consistent language context for all product updates
    const params = { lang: language };

    return this.useUrlSuffix('process')
      .update<Product>(product.id, product, { skipWrite: true, params })
      .pipe(
        map((p) => removeNilProperties(p)),
        tap((p) => {
          p = Product.create({ ...product, ...p });
          this.store.replace(p.id, p);
        }),
      );
  }

  public updateStatus(product, releaseDate?): Observable<Partial<Product>> {
    const status = product.status === 'draft' ? 'pre_release' : 'draft';
    const formData = new FormData();
    formData.append('status', status);
    if (releaseDate) {
      formData.append('releaseDate', normalizeDate(releaseDate));
    }

    return this.useUrlSuffix(`${product.id || product.uid}/update`)
      .add<Product>(formData)
      .pipe(
        map((p) => removeNilProperties(p)),
        tap((p) => {
          p = Product.create({ ...product, ...p });
          this.store.replace(p.id, p);
        }),
      );
  }

  public updateShortProfile(product: Product): Observable<Partial<Product>> {
    product = Product.create({
      ...product,
      productName: product.productName || this.translate.instant('INTL_UNDEFINED_NAME'),
    });

    return this.useUrlSuffix('short-profile')
      .update<Product>(product.id, product, { skipWrite: true })
      .pipe(
        map((p) => removeNilProperties(p)),
        tap((p) => {
          p = Product.create({ ...product, ...p });
          this.store.replace(p.id, p);
        }),
      );
  }

  public updateShortProfileWithLanguage(product: Product, language: string): Observable<Partial<Product>> {
    product = Product.create({
      ...product,
      productName: product.productName || this.translate.instant('INTL_UNDEFINED_NAME'),
    });

    // Build query parameters
    const params: any = {};

    // Always pass language parameter when editing a product
    // This ensures consistent language context for all product updates
    params.lang = language;

    // Use undefined if no parameters to avoid empty object
    const finalParams = Object.keys(params).length > 0 ? params : undefined;

    return this.useUrlSuffix('short-profile')
      .update<Product>(product.id, product, { skipWrite: true, params: finalParams })
      .pipe(
        map((p) => removeNilProperties(p)),
        tap((p) => {
          p = Product.create({ ...product, ...p });
          this.store.replace(p.id, p);
        }),
      );
  }

  public deleteProduct(product: Product): Observable<Partial<Product>> {
    return this.useUrlSuffix('delete')
      .delete<Product>(product.id)
      .pipe(
        tap(() => {
          this.store.remove(product.id);
        }),
        catchError((error) => {
          this.snackBarService.error('INTL_ERROR_DELETING_PRODUCT');
          return throwError(error);
        }),
      );
  }
  public duplicateProduct(product: Product): Observable<Partial<Product>> {
    const formData = new FormData();
    formData.append('status', 'draft');
    return this.useUrlSuffix(`${product.id}/duplicate`)
      .add<Product>(formData)
      .pipe(
        tap((data) => {
          this.store.add(data);
        }),
        catchError((error) => {
          this.snackBarService.error('INTL_ERROR_DELETING_PRODUCT');
          return throwError(error);
        }),
      );
  }

  private getPart(part: Subresource, productId: Product['id'], queryParams?: any): Observable<Product> {
    return this.useUrlSuffix(part).get<Product>(productId, { skipWrite: true, params: queryParams });
  }

  private mergeProductSubresources(
    product: Product,
    opening: Product,
    shortProfile: Product,
    process: Product,
  ): Product {
    const openingData = removeNilProperties(opening);
    const baseProduct = removeNilProperties(product);
    const availableTranslations = openingData.availableTranslations || [];

    return Product.create(
      Object.assign(
        {},
        baseProduct,
        openingData,
        removeNilProperties(shortProfile),
        removeNilProperties(process),
        {
          availableTranslations: [...availableTranslations],
          keepPrivate: openingData.keepPrivate ?? baseProduct.keepPrivate ?? false,
          clientCompany: openingData.clientCompany ?? baseProduct.clientCompany ?? null,
        },
      ),
    );
  }

  private removeUnnecessaryProperties(product: Product): Partial<Product> {
    // do not overwrite store for properties not present in `opening` subresource
    delete product.shortProfileDescription;
    delete product.processDescription;

    // remove as backend does not contain `status` field in a response
    // when updating resource so, in consequence, it will be `null` in Akita's store
    delete product.status;

    return product;
  }

  public createProduct(product: Product): Observable<Product> {
    // Ensure the product has the correct default language set based on current language
    const currentLanguageCode = this.languageService.getCurrentLanguage();
    const availableLanguages = this.languageService.getAvailableLanguages();

    // If product doesn't have defaultLanguage set, use current language
    if (!product.defaultLanguage) {
      // Find the language object that matches the current language code
      const currentLanguage = availableLanguages.find((lang) => lang.code === currentLanguageCode);

      if (currentLanguage && currentLanguage.id) {
        product.defaultLanguage = currentLanguage.id;
      } else {
        // Fallback to common language ID mappings if language is not found in available languages
        const languageCodeMap: { [key: string]: number } = {
          en: 1, // English
          de: 2, // German
          fr: 3, // French
          es: 4, // Spanish
          it: 5, // Italian
          pt: 6, // Portuguese
          nl: 7, // Dutch
          pl: 8, // Polish
          ru: 9, // Russian
          zh: 10, // Chinese
          ja: 11, // Japanese
          ko: 12, // Korean
        };

        const fallbackId = languageCodeMap[currentLanguageCode];
        if (fallbackId) {
          product.defaultLanguage = fallbackId;
        } else {
          product.defaultLanguage = 1; // Ultimate fallback to English
        }
      }
    }

    // Create the product with language parameter
    return this.add<Product>(product, {
      params: { lang: currentLanguageCode },
    }).pipe(
      tap((createdProduct) => {
        // Store the language in product-specific key only
        // This ensures each product maintains its own language context
        const productLanguageKey = `product_${createdProduct.id}_language`;
        localStorage.setItem(productLanguageKey, currentLanguageCode);
      }),
    );
  }
}
